<!--
  A branch history's header (views/RefView.svelte): the ref and what it is
  (a branch, the default one marked, a tag, or a commit), the ref a picker
  of another (components/RefPicker.svelte, `, b`), the path its history is
  filtered to as a breadcrumb (each folder narrowing to it, and the whole
  history), its latest
  commit and that commit's checks, the open pull requests from the branch,
  the way to compare it with the default branch, its two views, its
  local agents, and the way to ask one about the whole history. Everything it shows is the store's.
-->
<script lang="ts">
  import type { RefStore } from '../state/ref.svelte';
  import { checksState, failingCount } from '../logic/checks';
  import { relTime } from '../logic/time';
  import { subjectKey, type SubjectKey } from '../api/relaySubject';
  import { prPath, subjectPath } from '../router';
  import { carryFilter } from '../logic/filefilter';
  import { nav } from '../nav.svelte';
  import { session } from '../state/session.svelte';
  import { pathTrail } from '../jump/pick';
  import RelayPrAgents from './RelayPrAgents.svelte';
  import RefPicker from './RefPicker.svelte';

  interface Props {
    store: RefStore;
    view: 'files' | 'commits';
    /** Opens (or closes) the page's agent panel about the whole history; null where no agent can be asked. */
    onask?: (() => void) | null;
    /** Whether the ref's picker is open (the page opens it by key). */
    picking?: boolean;
  }
  let { store, view, onask = null, picking = $bindable(false) }: Props = $props();

  const s = $derived(store.subject);
  const KIND = { branch: 'Branch', tag: 'Tag', commit: 'Commit' } as const;
  /** The ref as a sentence names it: a commit by its short SHA. */
  const name = $derived(store.kind === 'commit' ? s.ref.slice(0, 7) : s.ref);
  const latest = $derived(store.track.main[store.track.main.length - 1] ?? null);
  const checks = $derived(store.checks ? checksState(store.checks.contexts) : null);
  const failing = $derived(store.checks ? failingCount(store.checks.contexts) : 0);
  /** The compare of this branch with the default branch, when it is another branch. */
  const compare = $derived(
    store.kind === 'branch' && store.defaultBranch && !store.isDefault ? subjectPath({ kind: 'compare', owner: s.owner, repo: s.repo, base: store.defaultBranch, head: s.ref, headRepo: null }) : null,
  );
  const webUrl = $derived(session.config?.github_web_url ?? 'https://github.com');
  const key = $derived(subjectKey(s) as SubjectKey);
  let agentsOpen = $state(false);
  /** The path filter as a breadcrumb; null for the whole history. */
  const trail = $derived(pathTrail(s, view, nav.query));
  const tabs = $derived([
    { view: 'commits' as const, label: 'Commits', count: store.history ? store.history.commits.length + (store.track.hasOlder ? '+' : '') : null },
    { view: 'files' as const, label: 'Files', count: null },
  ]);
</script>

<header class="pr-head ref-head">
  <div class="pr-head-row">
    <div style="min-width:0;display:grid;gap:8px">
      <!-- Named apart from its controls: the same name whether the picker is open or not. -->
      <h1 class="pr-title" aria-label="{KIND[store.kind]} {name}">
        <span class="muted">{KIND[store.kind]}</span>
        <RefPicker label={name} pick={{ subject: s, end: 'ref', view, query: nav.query, defaultBranch: store.defaultBranch }} bind:open={picking} />
      </h1>
      {#if trail}
        <nav class="ref-trail" aria-label="Path filter">
          <ol>
            <li><a href={trail.whole}>Whole history</a></li>
            {#each trail.parts as p (p.href)}
              <li>{#if p.current}<code aria-current="page">{p.name}</code>{:else}<a href={p.href}><code>{p.name}</code></a>{/if}</li>
            {/each}
          </ol>
        </nav>
      {/if}
      <div class="pr-sub">
        <span>History</span>
        {#if store.isDefault}<span class="pill">Default branch</span>{/if}
        {#if checks && checks !== 'none'}
          <span class="pill checks-{checks}">{checks === 'success' ? 'Checks passed' : checks === 'failure' ? `${failing} check${failing === 1 ? '' : 's'} failing` : 'Checks running'}</span>
        {/if}
      </div>
      {#if latest}
        <div class="pr-sub latest">
          <span class="muted">Latest{s.path === null ? '' : ' here'}:</span>
          <code>{latest.short}</code>
          <span class="latest-message">{latest.message ?? ''}</span>
          {#if latest.author}<span class="muted">{latest.author}</span>{/if}
          {#if latest.at}<span class="muted">{relTime(latest.at)}</span>{/if}
        </div>
      {/if}
    </div>
    <div class="pr-actions">
      {#if session.agents}
        <button class="btn small" aria-expanded={agentsOpen} aria-controls="ref-agents" onclick={() => (agentsOpen = !agentsOpen)}>Local agents</button>
        {#if onask}<button class="btn small" onclick={onask}>Ask agent</button>{/if}
      {/if}
      {#each store.pulls ?? [] as p (p.number)}
        <a class="btn small" href={prPath(s.owner, s.repo, p.number)} title={p.title}>Pull request #{p.number} (into {p.base})</a>
      {/each}
      {#if compare}<a class="btn small" href={compare}>Compare with {store.defaultBranch}</a>{/if}
      <a class="btn small" href="{webUrl}/{s.owner}/{s.repo}/commits/{encodeURIComponent(s.ref).replace(/%2F/gi, '/')}" target="_blank" rel="noreferrer" data-external>GitHub ↗</a>
    </div>
  </div>
  {#if agentsOpen && session.agents}<div id="ref-agents"><RelayPrAgents subject={key} embedded /></div>{/if}
  <nav class="ref-tabs" aria-label="History views">
    {#each tabs as t (t.view)}
      <a href={carryFilter(subjectPath(s, t.view), nav.query)} aria-current={t.view === view ? 'page' : undefined}>{t.label}{#if t.count !== null}{' '}<span class="count">{t.count}</span>{/if}</a>
    {/each}
  </nav>
</header>

<style>
  .count {
    color: var(--muted);
    font-size: 12px;
  }
  .checks-success {
    color: var(--good);
  }
  .checks-failure {
    color: var(--bad);
  }
  .ref-trail ol {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 0;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 13px;
    min-width: 0;
  }
  .ref-trail li {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .ref-trail li + li::before {
    content: '/';
    margin: 0 6px;
    color: var(--muted);
  }
  .ref-trail a {
    display: inline-block;
    padding: 2px 0;
  }
  .ref-trail code {
    font-size: 12px;
  }
  .ref-trail [aria-current='page'] {
    font-weight: 650;
  }
  .latest {
    min-width: 0;
  }
  .latest-message {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
